import CardOnFile from '@liveauctioneers/caterwaul-components/lib/CardOnFile/CardOnFile';
import InPageMessage from '@liveauctioneers/caterwaul-components/lib/InPageMessage/InPageMessage';
import { BankAccount } from '@liveauctioneers/caterwaul-components/types/BankAccount';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { Radio } from '@liveauctioneers/hammer-ui-core/radio';
import { H3 } from '@liveauctioneers/hammer-ui-core/text';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import BankAccountStatus from '@/enums/bankAccountStatus';
import useIsServerSideRendered from '@/hooks/useIsServerSideRendered';
import { usePrevious } from '@/hooks/usePrevious';
import { useAppSelector } from '@/redux/hooks';
import { getAuthFailedCardIds } from '@/redux/modules/registerForCatalog';
import { CreditCard } from '@/types/CreditCard';
import noop from '@/utils/noop';

import { BankAccountOnFileWithPlaidVerifyContainer } from '../Plaid/BankAccountOnFileWithPlaidVerifyContainer';

type Props = {
    backgroundColor?: string;
    backgroundColorSelected?: string;
    bankAccounts?: BankAccount[];
    borderColor?: string;
    creditCards?: CreditCard[];
    deleteBankAccount?: (bankAccountId: number) => void;
    deleteCard?: (cardId: number) => void;
    paymentError?: React.ReactNode;
    selectBankAccount?: (bankAccount: BankAccount) => void;
    selectCard?: (creditCard: CreditCard) => void;
    selectedCard?: CreditCard;
    title?: ReactNode;
    validationError?: boolean;
};

export const PaymentMethodSelection = ({
    bankAccounts,
    creditCards,
    deleteBankAccount,
    deleteCard,
    paymentError,
    selectBankAccount = noop,
    selectCard = noop,
    selectedCard = null,
    title = null,
}: Props) => {
    const authFailedCardIds: number[] = useAppSelector((state) => getAuthFailedCardIds(state));
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>(
        selectedCard ? `card-${selectedCard?.id}` : ''
    );
    const prevSelectedCard = usePrevious(selectedCard);
    const prevCardsLength = usePrevious(creditCards?.length);
    const prevBankAccountsLength = usePrevious(bankAccounts?.length);
    const isSSR = useIsServerSideRendered();

    useEffect(() => {
        if (
            !selectedPaymentMethodId ||
            prevCardsLength !== creditCards?.length ||
            prevSelectedCard?.id !== selectedCard?.id ||
            prevBankAccountsLength !== bankAccounts?.length
        ) {
            if (creditCards) {
                const defaultCardOnFile = creditCards.find((cof) => cof.defaultCard);
                setSelectedPaymentMethodId(selectedCard ? `card-${selectedCard?.id}` : `card-${defaultCardOnFile?.id}`);
                //just to make sure the parent is on the same page.
                if (!selectedPaymentMethodId && Boolean(defaultCardOnFile?.id) && selectCard) {
                    selectCard(defaultCardOnFile);
                }
            } else if (bankAccounts) {
                const defaultBankAccount = bankAccounts.find((bankAccount) => bankAccount.defaultAccount);
                setSelectedPaymentMethodId(`bankAccount-${defaultBankAccount?.bankAccountId}`);
                //just to make sure the parent is on the same page.
                if (defaultBankAccount && selectBankAccount) {
                    selectBankAccount(defaultBankAccount);
                }
            }
        }
    }, [
        bankAccounts,
        creditCards,
        prevBankAccountsLength,
        prevCardsLength,
        selectBankAccount,
        selectCard,
        selectedPaymentMethodId,
        selectedCard,
        prevSelectedCard?.id,
    ]);

    const mapRadio = useCallback(
        (id: number, radioId: string, selected: boolean, disabled: boolean, child: ReactNode, onChange: () => void) => {
            return (
                <Radio.Option
                    checked={selected}
                    className="mb-md w-full items-start pl-[25px] pt-xs [&_input]:mt-[36px]"
                    disabled={disabled}
                    key={id}
                    name="paymentMethod"
                    onChange={onChange}
                    value={radioId}
                >
                    {child}
                </Radio.Option>
            );
        },
        []
    );

    const creditCardRadios = useMemo(() => {
        if (!creditCards) {
            return null;
        }
        // const sortedcreditCards = creditCards.sort((cardA, cardB) => {
        //     //TODO add created to the type
        //     return new Date(cardB.created).getTime() - new Date(cardA.created).getTime();
        // });
        return creditCards.map((creditCard) => {
            const radioId = `card-${creditCard.id}`;
            const selected = selectedPaymentMethodId === radioId;
            const failedAuth = authFailedCardIds.includes(creditCard.id);
            const label = isSSR ? (
                <div className="flex h-[98px] items-center justify-center pb-md">
                    <Loading />
                </div>
            ) : (
                <CardOnFile
                    creditCard={creditCard}
                    deleteCard={
                        deleteCard
                            ? (event) => {
                                  event.stopPropagation();
                                  if (selected) {
                                      setSelectedPaymentMethodId(undefined);
                                  }
                                  deleteCard(creditCard.id);
                              }
                            : undefined
                    }
                    deleteCardLinkText="Remove"
                    isDefault={creditCard.defaultCard}
                    isRadio
                    processingErr={failedAuth}
                />
            );
            return mapRadio(creditCard.id, radioId, selected, failedAuth, label, () => {
                setSelectedPaymentMethodId(radioId);
                selectCard(creditCard);
            });
        });
    }, [creditCards, selectedPaymentMethodId, authFailedCardIds, isSSR, deleteCard, mapRadio, selectCard]);

    const bankAccountRadios = useMemo(() => {
        if (!bankAccounts) {
            return null;
        }
        return bankAccounts.map((bankAccount) => {
            const radioId = `bankAccount-${bankAccount.bankAccountId}`;
            const selected = selectedPaymentMethodId === radioId;
            return mapRadio(
                bankAccount.bankAccountId,
                radioId,
                selected,
                bankAccount.status === BankAccountStatus.Pending ||
                    bankAccount.status === BankAccountStatus.PendingManual ||
                    bankAccount.status === BankAccountStatus.PendingAutomatic,
                <BankAccountOnFileWithPlaidVerifyContainer
                    bankAccount={bankAccount}
                    deleteAccount={
                        deleteBankAccount
                            ? (event) => {
                                  event.stopPropagation();
                                  if (selected) {
                                      setSelectedPaymentMethodId(undefined);
                                  }
                                  deleteBankAccount(bankAccount.bankAccountId);
                              }
                            : undefined
                    }
                    deleteAccountLinkText="Remove"
                />,
                () => {
                    setSelectedPaymentMethodId(radioId);
                    selectBankAccount(bankAccount);
                }
            );
        });
    }, [bankAccounts, deleteBankAccount, mapRadio, selectBankAccount, selectedPaymentMethodId]);

    return (
        <>
            {title && (
                <H3
                    className="mt-[10px]"
                    color="secondary"
                >
                    {title}
                </H3>
            )}
            <div>
                {/* Not using Radios component here so we can fully control it ourselves. */}
                {creditCardRadios}
                {bankAccountRadios}
                {Boolean(paymentError) && (
                    <InPageMessage
                        className="mt-[10px]"
                        type="error"
                    >
                        {paymentError}
                    </InPageMessage>
                )}
            </div>
        </>
    );
};
